import { getGetApiInstitutionQueryOptions, type InstitutionViewDTO } from '@smartsash/api-client';
import { defineDataScreen, pagedQuery } from '@smartsash/data-screen';
import { Avatar, Button, Icon } from '@smartsash/ui';
import { Link, useNavigate } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import styles from './Institutions.module.css';
import { InstitutionsFilter } from './InstitutionsFilter';
import {
  institutionsColumnText,
  institutionsQueryParams,
  institutionsSearchSchema,
  type InstitutesColumnId,
} from './institutionsModel';
import MobileCard from './MobileCard';
import RowActions from './RowActions';

import { useDataScreenTranslate } from '@/hooks/useDataScreenTranslate';
import { ContactLink } from '@/ui/shared/ContactLink';
import { apiAssetUrl } from '@/utils/apiAssetUrl';

/** Headers render rather than resolve: a module-scope definition cannot call `t`. */
function ColumnHeader({ column }: { column: InstitutesColumnId }) {
  const { t } = useTranslation();
  return <>{t(`institutes.columns.${column}`)}</>;
}

const columns: ColumnDef<InstitutionViewDTO>[] = [
  {
    id: 'company',
    header: () => <ColumnHeader column="company" />,
    cell: ({ row }) => (
      <Link
        to="/institutions/$id"
        params={{ id: row.original.id ?? '' }}
        className={styles['company-cell']}
      >
        {/* HS-1243: an uploaded logo appeared nowhere but the edit form's own drop zone, so from
            the panel's point of view setting one changed nothing. `Avatar` falls back to the
            initials when the institution has no logo, which is what the rows without one keep
            showing. */}
        <Avatar
          className={styles['company-logo']}
          src={apiAssetUrl(row.original.logoIconUrl)}
          name={row.original.name ?? ''}
          size="sm"
        />
        <span className={styles['company-text']}>
          <span className={styles['company-name']}>{row.original.name}</span>
          <span className={styles['company-code']}>{row.original.accountNumber}</span>
        </span>
      </Link>
    ),
    meta: { primary: true, sortable: true, value: institutionsColumnText.company },
  },
  {
    id: 'city',
    header: () => <ColumnHeader column="city" />,
    cell: ({ row }) => row.original.city,
    meta: { width: '160px', sortable: true, value: institutionsColumnText.city },
  },
  {
    id: 'contact',
    header: () => <ColumnHeader column="contact" />,
    cell: ({ row }) => (
      <div className={styles['contact-cell']}>
        <Avatar name={row.original.contact ?? ''} size="sm" />
        <span className={styles['contact-name']}>{row.original.contact}</span>
      </div>
    ),
    meta: { width: '200px', sortable: true, value: institutionsColumnText.contact },
  },
  {
    id: 'email',
    header: () => <ColumnHeader column="email" />,
    cell: ({ row }) => (
      <ContactLink type="email" value={row.original.email} className={styles['link-cell']} />
    ),
    meta: { width: '200px', sortable: true, value: institutionsColumnText.email },
  },
  {
    id: 'phone',
    header: () => <ColumnHeader column="phone" />,
    cell: ({ row }) => (
      <ContactLink type="phone" value={row.original.phoneNumber} className={styles['link-cell']} />
    ),
    meta: { width: '200px', value: institutionsColumnText.phone },
  },
];

function AddInstituteButton({ iconOnly = false }: { iconOnly?: boolean }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const goToNew = () => {
    void navigate({ to: '/institutions/new' });
  };

  return iconOnly ? (
    <Button
      hierarchy="primary"
      size="sm"
      iconLeading={<Icon name="plus" />}
      iconOnly
      aria-label={t('institutes.addAriaLabel')}
      onClick={goToNew}
    />
  ) : (
    <Button hierarchy="primary" size="sm" iconLeading={<Icon name="plus" />} onClick={goToNew}>
      {t('institutes.add')}
    </Button>
  );
}

/**
 * The Institutes list on `@smartsash/data-screen` (HS-869, slice 2 of the plan's entity order).
 *
 * List-only. `searchScope`/`sortScope` are the factory's `'server'` default: `GetApiInstitutionParams`
 * now carries `search`, `sortBy`/`sortDir` and a repeatable `city` filter — `institutionsQueryParams`
 * turns the URL into that query and is shared with `InstitutionsFilter`, which reads the same cache
 * entry and takes City's vocabulary from `/filter-fields` rather than from the loaded page.
 */
export const institutionsScreen = defineDataScreen({
  entity: 'institution',
  searchSchema: institutionsSearchSchema,
  useTranslate: useDataScreenTranslate,
  list: {
    query: (search) =>
      pagedQuery<InstitutionViewDTO>(
        getGetApiInstitutionQueryOptions(institutionsQueryParams(search)),
      ),
    columns,
    getRowId: (row) => row.id ?? '',
    mobileCard: (row) => <MobileCard row={row} />,
    titleKey: 'institutes.title',
    emptyState: {
      filteredTitleKey: 'list.emptyFiltered.title',
      filteredDescriptionKey: 'list.emptyFiltered.description',
      titleKey: 'institutes.empty.title',
      descriptionKey: 'institutes.empty.description',
    },
    selectable: true,
    rowActions: [{ id: 'row-actions', render: (row) => <RowActions row={row} /> }],
    headerAction: {
      id: 'add',
      permission: 'institution.create',
      render: () => <AddInstituteButton />,
    },
    mobileHeaderAction: {
      id: 'add-mobile',
      permission: 'institution.create',
      render: () => <AddInstituteButton iconOnly />,
    },
    filterSlot: (search) => <InstitutionsFilter search={search} />,
  },
});
